import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { devLog, devError } from '../../utils/devLogger';
import styled from 'styled-components';
import { isShoppingMode } from '../../config/appMode';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import LotCard from '../../customComponents/FavoriteCard';
import NoDataContainer from '../../components/NoDataContainer';
import DataLoader from '../../components/DataLoader';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import { LikedLotData, WonAuctionData } from '../../lib/api/user/userApi.types';
import { AppColors } from '../../styles/colors';

const WonAuctionDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: calc(100vh - 120px); /* 앱바와 바텀바 높이를 제외한 나머지 공간 */
`;

// 낙찰 Lots 그리드 (BidHistory와 동일)
const LotsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 24px 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
    padding: 32px 0;
  }
`;

// 다국어 텍스트
const wonAuctionDetailTexts = {
  ko: {
    auctionEnded: isShoppingMode() ? '판매종료' : '경매종료',
    // FavoriteCard용 텍스트들 (사용하지 않지만 필수)
    preSubmission: isShoppingMode() ? '구매하기' : '사전입찰하기',
    liveView: '라이브보기',
    liveBid: isShoppingMode() ? '구매하기' : '라이브입찰하기',
    liveStartPrice: isShoppingMode() ? '판매가' : '라이브 시작가',
    startPrice: isShoppingMode() ? '판매가' : '시작가',
    winningPrice: isShoppingMode() ? '결제금액' : '낙찰가',
    noData: '낙찰된 상품이 없습니다'
  },
  en: {
    auctionEnded: isShoppingMode() ? 'Sale Ended' : 'Auction Ended',
    // FavoriteCard용 텍스트들 (사용하지 않지만 필수)
    preSubmission: isShoppingMode() ? 'Purchase' : 'Pre-Bid',
    liveView: 'Live View',
    liveBid: isShoppingMode() ? 'Purchase' : 'Live Bid',
    liveStartPrice: isShoppingMode() ? 'Price' : 'Live Starting Price',
    startPrice: isShoppingMode() ? 'Price' : 'Starting Price',
    winningPrice: isShoppingMode() ? 'Payment Amount' : 'Winning Bid',
    noData: 'No won items'
  }
};

// 요일 다국어 텍스트
const dayNamesLocalized = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
};

// 시간 포맷 유틸리티 함수 (BidHistory와 동일)
const formatLiveTime = (liveTime: string, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const targetDate = new Date(liveTime);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    if (now >= targetDate) {
      return 'LIVE';
    }
    
    if (targetDay.getTime() === today.getTime()) {
      const diffMs = targetDate.getTime() - now.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`;
      } else {
        return `${diffMinutes}m`;
      }
    }
    
    const month = targetDate.getMonth() + 1;
    const date = targetDate.getDate();
    const dayNames = dayNamesLocalized[language];
    const dayName = dayNames[targetDate.getDay()];
    const hours = targetDate.getHours().toString().padStart(2, '0');
    const minutes = targetDate.getMinutes().toString().padStart(2, '0');
    
    return `${month}/${date}(${dayName}) ${hours}:${minutes}`;
  } catch (error) {
    return liveTime;
  }
};


// LiveEndTime 포맷팅 함수 (BidHistory와 동일)
const formatLiveEndTime = (dateStr: string, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const date = new Date(dateStr);
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // mm
    const day = date.getDate(); // d (패딩 없음)
    const hours = date.getHours().toString().padStart(2, '0'); // hh
    const minutes = date.getMinutes().toString().padStart(2, '0'); // MM
    const dayName = dayNamesLocalized[language][date.getDay()]; // 요일
    
    if (language === 'ko') {
      return `${month}/${day}(${dayName}) ${hours}:${minutes} 종료`;
    } else {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[date.getMonth()];
      return `${monthName} ${day}(${dayName}) ${hours}:${minutes} Ends`;
    }
  } catch (error) {
    return dateStr;
  }
};

// WonAuctionData의 lots를 LikedLotData 형식으로 변환하는 함수
const convertWonLotToLikedLot = (wonLot: any, auctionData: WonAuctionData): LikedLotData => {
  const convertedData = {
    index: wonLot.index,
    code: wonLot.code,
    name: wonLot.name,
    description: wonLot.description,
    images: wonLot.images || [],
    startPrice: wonLot.startPrice,
    exchangeRate: wonLot.exchangeRate || 1480.73, // 기본 환율
    startPriceUsd: wonLot.startPriceUsd || (wonLot.startPrice / 1480.73),
    recommendNo: wonLot.recommendNo || 0,
    likedUserIndexes: wonLot.likedUserIndexes || [],
    likeCount: wonLot.likeCount || 0,
    createBy: wonLot.createBy || '',
    updateBy: wonLot.updateBy || '',
    createAt: wonLot.createAt || '',
    updateAt: wonLot.updateAt || "",
    category: wonLot.category || { code: '', name: '', krName: '' },
    subCategory: wonLot.subCategory || { code: '', name: '', krName: '' },
    auction: {
      index: auctionData.index,
      name: auctionData.name,
      description: auctionData.description,
      url: auctionData.url,
      auctionLotNo: 1,
      preBidStartAt: auctionData.preBidStartAt,
      preBidEndAt: auctionData.preBidEndAt,
      auctionStartAt: auctionData.auctionStartAt,
      auctionEndAt: auctionData.auctionEndAt,
      paymentDueAt: auctionData.paymentDueAt,
      auctionFeeRate: auctionData.auctionFeeRate,
      platformFeeRate: auctionData.platformFeeRate,
      createAt: auctionData.createAt,
      updateAt: auctionData.updateAt || ''
    },
    auctioneer: {
      index: auctionData.auctioneer.index,
      name: auctionData.auctioneer.name,
      description: '',
      profilePic: ''
    }
  };

  // apiData에 ended 상태와 낙찰가(amount) 추가
  (convertedData as any).apiData = {
    ended: true, // 낙찰된 상품이므로 항상 종료 상태
    highestBid: wonLot.amount // lots의 amount가 낙찰가
  };

  return convertedData;
};

const WonAuctionDetail: React.FC = () => {
  const { lang } = useLang();
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { auctionIndex } = useParams<{ auctionIndex: string }>();
  const texts = wonAuctionDetailTexts[lang];
  
  // 가격 포맷팅 함수 - 사용자 locationType에 따라 KRW 또는 USD 표시
  const formatPrice = (price: number, language: 'ko' | 'en', exchangeRate: number): string => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    if (isDomestic) {
      return `KRW ${price.toLocaleString()}`;
    } else {
      // USD로 표시 (환율 정보 활용) - 소수점 2자리까지 표시
      const usdPrice = price / exchangeRate;
      return `USD ${usdPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };
  
  // location.state에서 auctionData 가져오기
  const auctionData = location.state?.auctionData as WonAuctionData;
  
  // 상태 관리
  const [wonLots, setWonLots] = useState<LikedLotData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 낙찰 Lots 데이터 로드
  const fetchWonLots = useCallback(async () => {
    if (!auctionData) {
      setError('경매 데이터가 없습니다.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // auctionData의 lots를 LikedLotData 형식으로 변환
      if (auctionData.lots && Array.isArray(auctionData.lots)) {
        const convertedLots = auctionData.lots.map(lot => {
          const convertedLot = convertWonLotToLikedLot(lot, auctionData);
          // 낙찰상세에서는 모든 아이템이 ended 상태
          (convertedLot as any).ended = true;
          return convertedLot;
        });
        setWonLots(convertedLots);
      } else {
        setWonLots([]);
      }
      
    } catch (error) {
      devError('낙찰 상세 데이터 처리 실패:', error);
      setError('데이터를 처리하는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [auctionData]);

  useEffect(() => {
    fetchWonLots();
  }, [fetchWonLots]);

  // 페이지 제목 설정
  useEffect(() => {
    const originalTitle = document.title;
    document.title = lang === 'ko' ? '낙찰 Lots' : 'Won Lots';
    
    return () => {
      document.title = originalTitle;
    };
  }, [lang]);

  // 더미 핸들러들 (버튼 클릭을 방지)
  const handleCardClick = (item: LikedLotData) => {
    // 낙찰상세에서는 카드 클릭 무시
    devLog('낙찰 상세 카드 클릭 (무시):', item);
  };

  const handleFavoriteToggle = async (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    // 낙찰상세에서는 좋아요 기능 사용하지 않음
  };

  const renderWonLotCard = (item: LikedLotData, index: number) => {
    return (
      <LotCard
        key={`won-lot-${item.index}-${index}`}
        item={item}
        auctionStatus='ended' // 항상 ended 상태
        texts={{
          preSubmission: texts.preSubmission,
          liveView: texts.liveView,
          liveBid: texts.liveBid,
          auctionEnded: texts.auctionEnded,
          liveStartPrice: '', // 라이브 시작가 숨김
          startPrice: texts.startPrice, // 시작가 표시
          winningPrice: texts.winningPrice // 낙찰가 추가
        }}
        exchangeRate={item.exchangeRate}
        formatLiveTime={formatLiveTime}
        formatPrice={formatPrice}
        formatLiveEndTime={formatLiveEndTime}
        onCardClick={handleCardClick}
        onFavoriteToggle={handleFavoriteToggle}
        isBidHistory={false} // 취소 버튼 등은 보이지 않게
        showFavoriteIcon={false} // 좋아요 아이콘 숨김
      />
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 200px)',
          width: '100%'
        }}>
          <DataLoader/>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          minHeight: 'calc(100vh - 200px)'
        }}>
          <NoDataContainer message={texts.noData} imageSize={107} />
        </div>
      );
    }

    if (wonLots.length === 0) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          minHeight: 'calc(100vh - 200px)'
        }}>
          <NoDataContainer message={texts.noData} imageSize={107} />
        </div>
      );
    }

    return (
      <LotsGrid>
        {wonLots.map(renderWonLotCard)}
      </LotsGrid>
    );
  };

  const handleBack = () => {
    navigate('/mypage');
  };

  const MainContent = (
    <WonAuctionDetailContainer>
      <ProjectAppbar 
        title={lang === 'ko' ? '낙찰 Lots' : 'Won Lots'}
        currentPage="auction"
        showBackButton={true}
        onBack={handleBack}
      />
      <ContentContainer>
        {renderContent()}
      </ContentContainer>
    </WonAuctionDetailContainer>
  );

  return (
    <MainLayout>
      <CenteredLayout>
        <ResponsiveView
          mobileView={MainContent}
          desktopView={MainContent}
        />
      </CenteredLayout>
    </MainLayout>
  );
};

export default WonAuctionDetail;